import { RESPONSE_BADDATA, RESPONSE_NOTFOUND } from 'front-end-core/constants/responses'
import Route from 'route-parser'
import { of, combineLatest } from 'rxjs'
import { map, distinctUntilChanged, switchMap } from 'rxjs/operators'
import { result } from 'libraries/lodash'
import { isValidResponse, mapResponse } from 'front-end-core/helpers/dataflow'
import {
  CLARILIS_LINK,
  CLARILIS_LINK_PREPAY,
  CLARILIS_DOCS,
  CLARILIS_DOCS_PREPAY,
  METADATA,
  METADATA_PREPAY,
  DOC_DRAFTER_AUTOMATED_LIST,
  INTERVIEW,
  DOWNLOAD_INTERVIEW,
  DOC_DRAFTER_SAVED_DOCS,
  GET_PREVIEW_LIST,
  GET_INTERVIEW_PREVIEW,
  INTERVIEW_METADATA,
  SAVED_INTERVIEW,
  DOC_DRAFTER_AUTOMATED_LIST_PREPAY,
  METADATA_SECTIONS,
  METADATA_SECTIONS_PREPAY
} from 'constants/requests'
import { URL_DOCUMENT, URL_METADATA, URL_SUBMIT_INTERVIEW } from 'constants/urls'
import { DEFAULT_FLAVOUR } from 'constants/flavours'
import { fetch, refresh, observe } from 'services/ApiService'
import observeRequest from 'helpers/fcObserveRequest'
import loggedRequestSwitcher from 'helpers/loggedRequestSwitcher'
import reduxStore from 'redux/store'

const metadataRoute = (props) => new Route(URL_METADATA).reverse(props)
const documentRoute = (props) => new Route(URL_DOCUMENT).reverse(props)

const precedentsTiledMenuCombineRequests = (isAdmin, removeEmpty = false, sectionResponse, precedentsResponse) => {
  const parents = result(precedentsResponse, 'parents', {})
  const rootSections =
    result(sectionResponse, ['nodes', sectionResponse.root, 'childSections'], [])
      .map(id => sectionResponse.nodes[id])

  const response = ({
    ...sectionResponse,
    rootSections: rootSections.map(section => ({
      ...section,
      active: (section.active || isAdmin || !!(section.childSections.filter(childSectionId => {
        return result(parents, childSectionId, []).length
      }).length)),
      empty: !(section.childSections.filter(childSectionId => {
        return result(parents, childSectionId, []).length
      }).length)
    }))
  })
  return !removeEmpty
    ? response
    : { ...response, rootSections: response.rootSections.filter(item => !item.empty) }
}

export const requestPrecedentsTiledMenu = ({ library, aol }) => {
  const reduxState = reduxStore.getState()
  const isAdmin = reduxState.user.isAdmin

  const responseMapper = precedentsTiledMenuCombineRequests.bind(null, isAdmin, false) // responseMapper is the same function except the first arg is automatically set

  return combineLatest(
    [
      requestPrecedentSections(aol),
      requestPrecedents(aol)
    ],
    mapResponse(responseMapper)
  )
}

export const requestDocDrafterTiledMenu = ({ aol }) => {
  const responseMapper = precedentsTiledMenuCombineRequests.bind(null, false, true) // never admin for doc drafter

  return combineLatest(
    [
      requestPrecedentSections(aol),
      requestDocDrafterAutomatedListings(aol)
    ],
    mapResponse(responseMapper)
  )
}

// Request section example.
export const requestSectionPrecedents = (_, sectionId, aol) => {
  return combineLatest([requestPrecedentSections(aol), requestPrecedents(aol)], mapResponse(
    (sections, precedents) => {
      // Find the root ID of the section we're requesting
      const childSections = result(sections, ['nodes', sectionId, 'childSections'], null)
      // Error handle
      if (childSections === null) {
        return RESPONSE_NOTFOUND
      }
      return { ...precedents, ...sections, childSections, sectionId }
    }
  ))
}

export const refreshSectionPrecedents = (aol) => combineLatest(
  refreshPrecedentSections(DEFAULT_FLAVOUR, aol),
  refreshPrecedents(aol)
)

export const requestPrecedents = (aol) => loggedRequestSwitcher(() => fetch(METADATA, { aol }, {}, { _: Date.now() }), () => fetch(METADATA_PREPAY, { aol }, {}, { _: Date.now() }))
export const refreshPrecedents = (aol) => loggedRequestSwitcher(() => refresh(METADATA, { aol }, {}, { _: Date.now() }), () => refresh(METADATA_PREPAY, { aol }, {}, { _: Date.now() }))

export const submitPrecedent = function ({ parentId, documentId, description, title, file = null, name, library = 'precedent', position, tiers }, aol) {
  if (!parentId || !title || !position || !library || !name || !tiers) {
    return of(JSON.stringify({ success: false }))
  }

  let data
  if (file) {
    data = new FormData()
    data.append('file', file)
    data.append('name', file.name)
    // Metadata
    data.append('title', title)
    data.append('description', description)
    data.append('library', library)
    data.append('parentId', parentId)
    data.append('position', position.toString())
    data.append('tiers', tiers.filter(a => a.checked).map(a => a.value))
  } else {
    data = {
      name,
      lastUpdateTime: Date.now(),
      tiers: tiers.filter(a => a.checked).map(a => a.value),
      properties: {
        title,
        library,
        parentId,
        description,
        position: position.toString()
      }
    }
  }

  const url = file
    ? documentRoute({ id: documentId, aol })
    : metadataRoute({ id: documentId, aol })

  const body = file
    ? data
    : JSON.stringify(data)

  const headers = file
    ? {}
    : { 'content-type': 'application/json' }

  return observeRequest({
    url,
    method: 'POST',
    body,
    headers
  })
}

export const updatePrecedentPositions = function (precedentList, aol) {
  const data = precedentList.map((precedent) => {
    const { parentId, id, description, title, name, library, position, tiers } = precedent
    if (!library || !name || !title || !id || !parentId || (!position && position !== 0) || !tiers) {
      return of(JSON.stringify({ success: false }))
    }
    return {
      id,
      name,
      lastUpdateTime: Date.now(),
      tiers,
      properties: {
        title,
        library,
        parentId,
        description,
        position: position.toString()
      }
    }
  })

  return observeRequest({
    url: metadataRoute({ aol }),
    method: 'POST',
    body: JSON.stringify(data),
    headers: { 'content-type': 'application/json' }
  })
}

export const previewPrecedent = function (precedentId) {
  return of(RESPONSE_BADDATA)
}

export const requestClarilisLink = () => loggedRequestSwitcher(
  () => fetch(CLARILIS_LINK),
  () => fetch(CLARILIS_LINK_PREPAY)
)

export const requestClarilisDocs = () => loggedRequestSwitcher(
  () => fetch(CLARILIS_DOCS),
  () => fetch(CLARILIS_DOCS_PREPAY)
)

export const requestDocDrafterAutomatedListings = (aol) => loggedRequestSwitcher(
  () => fetch(DOC_DRAFTER_AUTOMATED_LIST, { aol }),
  () => fetch(DOC_DRAFTER_AUTOMATED_LIST_PREPAY, { aol })
)

export const getAutomatedListingTree = ({ aol, sectionId }) => {
  const combineRequests = (sections, documents) => {
    const childSections = result(sections, ['nodes', sectionId, 'childSections'], null)

    return { sections, documents, childSections, sectionId }
  }

  return combineLatest(
    [requestPrecedentSections(aol),
      requestDocDrafterAutomatedListings(aol)],
    mapResponse(combineRequests)
  )
}

export const getInterviewPath = ({ aol, interviewId = null, sectionId = null }) => combineLatest(
  [requestPrecedentSections(aol),
    requestInterviewMetaData({ aol, interviewId })],
  mapResponse(({ nodes }, interviewMetaData) => {
    const parent = nodes[interviewMetaData.parentSectionId]
    const grandParent = nodes[parent.path[0]]

    return [grandParent, parent].map(({ id, title }) => ({ id, title }))
  })
)

export const getAutomatedListingPath = ({ aol, sectionId }) => requestPrecedentSections(aol)
  .pipe(map(mapResponse(({ nodes }) => {
    const { title, id } = nodes[sectionId]

    return [{ title, id }]
  })))

export const getInterviewPreview = ({ aol, guid }) => {
  const listToPreviewMap = (listResp) => {
    if (!isValidResponse(listResp)) {
      return of(listResp)
    }
    const file = listResp[0] // there doesn't seem to be a reliable way to find the correct preview so hopefully they only ever send one. might need testing...
    if (!file) {
      return of(RESPONSE_NOTFOUND)
    }
    return getPreviewFromFile({ aol, guid, fileId: file.fileId })
  }

  return getPreviewList({ aol, guid }).pipe(switchMap(listToPreviewMap))
}

const getPreviewList = ({ aol, guid }) => observe(GET_PREVIEW_LIST, { aol, guid }).pipe(distinctUntilChanged()) // "fixes" bug with observe

const getPreviewFromFile = ({ aol, fileId, guid }) => observe(GET_INTERVIEW_PREVIEW, { aol, fileId, guid }).pipe(distinctUntilChanged())

export const requestInterview = ({ aol, interviewId }) => observe(INTERVIEW, { aol, interviewId })

export const requestSavedInterview = ({ aol, saveId }) => observe(SAVED_INTERVIEW, { aol, saveId })

export const requestInterviewMetaData = ({ aol, interviewId }) => fetch(INTERVIEW_METADATA, { aol, interviewId })

export const requestInProgressInterview = ({ aol, guid }) => pushInterviewAnswers({ aol, guid, body: [] })

export const pushInterviewAnswers = ({ aol, guid, body }) => observeRequest({
  url: new Route(URL_SUBMIT_INTERVIEW).reverse({ aol, guid }),
  method: 'POST',
  body: JSON.stringify(body)
}).pipe(map(response => JSON.parse(response)))

export const saveInterview = ({ aol, guid, saveName }) => observeRequest({
  url: new Route(URL_SUBMIT_INTERVIEW).reverse({ aol, guid }),
  method: 'PUT',
  headers: {
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({ saveName })
})

export const requestDownloadInterview = ({ aol, guid, pdf: Pdf = false }) => observe(
  DOWNLOAD_INTERVIEW,
  {
    aol,
    guid
  },
  {
    Pdf
  }
)

export const getSavedDocuments = ({ aol }) => fetch(DOC_DRAFTER_SAVED_DOCS, { aol })

export const requestPrecedentSections = function (aol) {
  return loggedRequestSwitcher(
    () => fetch(METADATA_SECTIONS, { aol }),
    () => fetch(METADATA_SECTIONS_PREPAY, { aol })
  )
}

export const refreshPrecedentSections = function (aol) {
  return loggedRequestSwitcher(
    () => refresh(METADATA_SECTIONS, { aol }),
    () => refresh(METADATA_SECTIONS_PREPAY, { aol })
  )
}
